import React, { useEffect } from 'react';

import { Box, useDisclosure } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { SortOrder } from 'components/DataDisplay/NewTable/types';
import { PageInfo } from 'components/Navigation';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { GroupTicketDetails } from 'containers/Findings/Tickets/Components/GroupTicketDetails';
import { useTicketsContext } from 'containers/Findings/Tickets/Components/hooks/context';
import { useTicketsColumns } from 'containers/Findings/Tickets/Components/hooks/useTicketsColumns';
import { selectTickets } from 'containers/Findings/Tickets/selectors';
import { actions } from 'containers/Findings/Tickets/slice';
import { TicketsSearchParamsProps } from 'containers/Findings/Tickets/types';
import { useSearchParams } from 'hooks/useSearchParams';

export const TicketsTable = () => {
  const dispatch = useDispatch();
  const { tickets } = useSelector(selectTickets);

  const { updateActiveResource } = useActiveResourceActions();
  const { onOpenMetadata } = useActiveResourceContext();

  const {
    findingsColumns,
    issueColumns,
    currentRecord,
    setCurrentRecord,
  } = useTicketsColumns();
  const params = useSearchParams<TicketsSearchParamsProps>();
  const groupTicketDetailsDisclosure = useDisclosure();
  const { getTickets, sortOrder, sortField, updateValue } = useTicketsContext();

  useEffect(() => {
    getTickets();
  }, [getTickets]);

  const handleRowClick = row => {
    updateActiveResource({
      resourceType: row?.resource_type,
      resourceId: row?.resource_id,
      accountId: row?.account_id,
    });
    onOpenMetadata();
  };

  return (
    <Box flex={1} h="full">
      <Table
        data={tickets.data}
        columns={params?.viewBy === 'findings' ? findingsColumns : issueColumns}
        isLoading={tickets.isLoading}
        cursor="pointer"
        styles={{ header: { zIndex: 800 } }}
        sortBy={{ sortOrder: sortOrder as SortOrder, sortField }}
        onSort={sortInfo => {
          if (
            !isEqual({ sortOrder: sortOrder as SortOrder, sortField }, sortInfo)
          ) {
            updateValue(sortField, sortInfo.sortField);
            updateValue(sortOrder, sortInfo.sortOrder);
          }
        }}
        onRowClick={row => {
          if (params?.viewBy === 'findings') {
            groupTicketDetailsDisclosure.onOpen();
            setCurrentRecord(row);
          } else handleRowClick(row);
        }}
        pagination={{
          totalCount: tickets.page.totalCount,
          pageInfo: tickets.page.info,
          cursorBased: isAlertVaultEnabled(),
          onChange(info: PageInfo) {
            dispatch(actions.updateTicketPagination(info));
          },
        }}
      />
      {groupTicketDetailsDisclosure.isOpen && (
        <GroupTicketDetails
          isOpen={groupTicketDetailsDisclosure.isOpen}
          onClose={groupTicketDetailsDisclosure.onClose}
          ticket={currentRecord}
          handleRowClick={handleRowClick}
        />
      )}
    </Box>
  );
};
